import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';
import clsx from 'clsx';
import { House } from '../types';
import { useLocale } from '@/hooks/use-locale';

const fallbackImage = require('@assets/images/fallback/colocation_medium.png');
const lightningIcon = require('@assets/images/marketplace/lighting.svg');

interface HouseCardProps {
  house: House;
  isComingSoon?: boolean;
}

export const HouseCard: React.FC<HouseCardProps> = ({ house, isComingSoon = false }) => {
  const { t } = useTranslation();
  const { currentLocale } = useLocale();

  const handleCardClick = () => {
    window.location.href = `/${currentLocale}/shared-houses/${house.slug}`;
  };

  return (
    <div
      onClick={handleCardClick}
      className={clsx(
        'flex cursor-pointer flex-col overflow-hidden',
        'rounded-[24px] bg-bg-primaryMedium',
        'pb-4',
        'transition-all',
        'hover:scale-[1.02] hover:outline hover:outline-2 hover:outline-black'
      )}
    >
      {/* Image */}
      <div className="relative mb-4 h-[220px] w-full overflow-hidden">
        <img
          src={house.image || fallbackImage}
          alt={house.name}
          className="h-full w-full object-cover"
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            const target = e.target as HTMLImageElement;
            target.src = fallbackImage;
          }}
        />
      </div>
      <div className="relative px-3">
        {/* House Name */}
        <h3 className="mb-1 text-h5 text-N900">{house.name}</h3>

        {/* City */}
        {house.city && (
          <p className="mb-0.5 text-small font-medium text-N600">{house.city}</p>
        )}

        {/* Address */}
        <p className="mb-3 text-body text-N700">{house.address}</p>

        {/* Badge and Price Row */}
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-2">
          {/* Badge */}
          {isComingSoon ? (
            <div className="inline-flex items-center rounded-full bg-gradient-1 py-[3px] px-[6px] self-start">
              <span className="text-small font-medium text-N900">{t('js.features.marketplace.home.find_your_next_home_section.coming_soon')}</span>
            </div>
          ) : house.available_immediately ? (
            <div className="flex items-center gap-1 self-start">
              <div className='bg-bg-successLight p-1 rounded-full'>
                <ReactSVG
                  src={lightningIcon}
                  className="h-3 w-3 text-text-successHeavy"
                  beforeInjection={(svg) => {
                    svg.querySelectorAll('path').forEach((path) => {
                      path.setAttribute('fill', 'currentColor');
                    });
                  }}
                />
              </div>
              <span className="text-small font-medium text-text-successHeavy">
                {t('js.features.marketplace.home.find_your_next_home_section.available_immediately')}
              </span>
            </div>
          ) : house.available_on ? (
            <div className="inline-flex items-center rounded-full bg-N100 py-[3px] px-[6px] self-start">
              <span className="text-small font-medium text-N700">
                {t('js.features.marketplace.home.find_your_next_home_section.available_from')} {house.available_on}
              </span>
            </div>
          ) : null}

          {/* Price */}
          <div className="flex items-center gap-1 self-end">
            <span className="text-medium text-text-primaryMedium">{t('js.features.marketplace.home.find_your_next_home_section.from')}</span>
            <span className="text-lead1 text-text-primaryHeavy">{house.price}</span>
            <span className="text-medium text-text-primaryMedium">{t('js.features.marketplace.home.find_your_next_home_section.per_month')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
